import { Navigate, RouteObject, redirect } from 'react-router';

import NewCustomerSignupModal from '@/auth/components/NewCustomerSignupModal';
import loginAs from '@/auth/pages/LoginAsPage';
import LoginPage, { loginPageMiddleware } from '@/auth/pages/LoginPage';
import LogoutPage, { logoutPageLoader } from '@/auth/pages/LogoutPage';
import register from '@/auth/pages/RegisterPage';
import resetPassword from '@/auth/pages/ResetPasswordPage';
import selectDomain from '@/auth/pages/SelectDomainPage';
import validateEmail from '@/auth/pages/ValidateEmailPage';
import { RouteErrorElement } from '@/components/ErrorFallback';
import config from '@/config';
import consultation from '@/consultation/pages/ConsultationPage';
import expertRequest from '@/expertrequest/pages/ExpertRequestPage';
import expertRequestInvitations from '@/expertrequest/pages/expertRequestInvitations';
import expertRequestList from '@/expertrequest/pages/expertRequestList';
import expertRequestNew, {
  addMembers as expertRequestAddMembers,
} from '@/expertrequest/pages/expertRequestNew';
import AgreementAcceptance from '@/legal/components/AgreementAcceptance';
import MessagingPage, { messagingMiddleware } from '@/messaging/pages/MessagingPage';
import NotFoundPage from '@/pages/NotFoundPage';
import profile from '@/profile/pages/profile';
import profileConflicts from '@/profile/pages/profileConflicts';
import profileMerge from '@/profile/pages/profileMerge';
import profileUploader from '@/profile/pages/profileUploader';
import unregisteredProfile from '@/profile/pages/unregisteredProfile';
import search from '@/search/pages/SearchPage';

import admin from './admin';
import application from './application';
import browse from './browse';
import complianceTraining from './complianceTraining';
import consultations from './consultations';
import dashboard from './dashboard';
import engagements from './engagements';
import expertNetwork from './expertNetwork';
import expertPayments from './expertPayments';
import graphiql from './graphiql';
import harnessRoutes from './harnessRoutes';
import payoutDashboard from './payoutDashboard';
import projectDetails from './projectDetails';
import {
  LegacyRoute,
  MiddlewareContext,
  ProtectionFunction,
  agreementGateOnlyMiddleware,
  agreementsRequiredLegacy,
  agreementsWithEmailRequiredLegacy,
  allRequiredLegacy,
  allRequiredMiddleware,
  applyMiddlewares as applyMiddleware,
  legacyRouteAdapter,
  loginRequiredLegacy,
  loginRequiredMiddleware,
  superAdminRequiredLegacy,
} from './routesMiddleware';
import settings from './settings';
import team from './team';
import teamAbout from './teamAbout';
import teamSettings from './teamSettings';
import teams from './teams';

function createAccessRoutes(context: MiddlewareContext): RouteObject[] {
  const adaptLegacy = (route: LegacyRoute, protectionFn?: ProtectionFunction) =>
    legacyRouteAdapter(context, route, protectionFn);

  return [
    {
      path: '/',
      element: <Navigate to="/dashboard" replace />,
    },
    // Nothing in the app links here, but Welcome emails sent before ENG-5589 do.
    {
      path: '/become-an-expert',
      loader: () => redirect('/signup/expert'),
    },
    adaptLegacy(browse, agreementsRequiredLegacy),
    // ENG-5570. Gated because it renders a console with the viewer's own bearer
    // token injected, for any logged-in user in production. Ungated it is a
    // link-shaped bypass of the whole gate: a refusing expert can do every
    // product action through it. Tightening who may reach /graphiql at all is
    // ENG-5549's problem, not this one's.
    adaptLegacy(graphiql, agreementsRequiredLegacy),
    {
      path: '/health-check',
      element: <h1>Ok</h1>,
      loader: () => JSON.stringify({ status: 200 }),
    },
    {
      path: '/smb-signup',
      element:
        config.appEnv === 'development' ? (
          <NewCustomerSignupModal
            open={true}
            onClose={() => (window.location.href = '/')}
            onComplete={() => (window.location.href = '/dashboard')}
            expert={{ id: 'test', slug: 'test-expert', name: 'Test Expert' }}
          />
        ) : (
          <NotFoundPage />
        ),
    },
    {
      path: '/login/:type?',
      element: <LoginPage />,
      loader: applyMiddleware(context, [loginPageMiddleware]),
    },
    {
      path: '/:subdomain/login/:type?',
      element: <LoginPage />,
      loader: applyMiddleware(context, [loginPageMiddleware]),
    },
    adaptLegacy(resetPassword),
    adaptLegacy(selectDomain),
    {
      path: '/logout',
      element: <LogoutPage />,
      loader: logoutPageLoader,
    },
    {
      path: '/change_password',
      loader: ({ request }) => {
        const next = new URL(request.url).searchParams.get('next');
        return redirect(
          next ? `/settings/password?next=${encodeURIComponent(next)}` : '/settings/password'
        );
      },
    },
    adaptLegacy(loginAs, loginRequiredLegacy),
    // ENG-5570. Deliberately no Layout wrapper: Layout renders AccessHeader
    // unconditionally, and a nav bar is an exit from a surface that must not
    // have one. agreementGateOnly runs after loginRequired and bounces anyone
    // who does not owe an acceptance, so this is not the gate gating itself.
    {
      path: '/legal/accept',
      element: <AgreementAcceptance />,
      loader: applyMiddleware(context, [loginRequiredMiddleware, agreementGateOnlyMiddleware]),
    },
    // Old links live in sent email and bookmarks, so carry ?next= across or the
    // expert lands on /dashboard instead of wherever they were headed.
    ...['/legal_ack', '/legal_ack/*'].map((path) => ({
      path,
      loader: ({ request }: { request: Request }) =>
        redirect(`/legal/accept${new URL(request.url).search}`, 301),
    })),
    adaptLegacy(application, allRequiredLegacy),
    adaptLegacy(consultation, allRequiredLegacy),
    adaptLegacy(consultations, allRequiredLegacy),
    adaptLegacy(dashboard, allRequiredLegacy),
    ...engagements.map((route) => adaptLegacy(route, allRequiredLegacy)),
    // ENG-5642 moved the approvals inbox to `/approvals`. Without this the old
    // path is not retired, it is absorbed: `/engagements/:id` matches it with
    // `id='approvals'` and dead-ends on "This engagement isn't available", with
    // no way back. Production never had the route (the allowlist is empty
    // there), so this covers QA and preview links shared during the pilot.
    { path: '/engagements/approvals', loader: () => redirect('/approvals', 301) },
    adaptLegacy(payoutDashboard, allRequiredLegacy),
    // ENG-5570. The inline gate call in ExpertRequestPage only guards the
    // numeric-id branch, leaving request_add ungated. That route is "add me as
    // a candidate", which is the participation the EPA governs.
    adaptLegacy(expertRequest, agreementsRequiredLegacy),
    adaptLegacy(expertRequestNew, allRequiredLegacy),
    adaptLegacy(expertRequestAddMembers, allRequiredLegacy),
    // ENG-6310. Staff send the invitations the MCP prepared; signed in only.
    adaptLegacy(expertRequestInvitations, allRequiredLegacy),
    {
      path: '/messaging/:channelId?',
      element: config.enableChat ? <MessagingPage /> : <NotFoundPage />,
      loader: applyMiddleware(context, [allRequiredMiddleware, messagingMiddleware]),
    },
    // ENG-5570: profile view, /request and /message were ungated. The adapter
    // passes the protection fn to children, so this covers all three. Anonymous
    // access is preserved — the gate no-ops without a viewer.
    adaptLegacy(profile, agreementsRequiredLegacy),
    adaptLegacy(unregisteredProfile, allRequiredLegacy),
    adaptLegacy(profileConflicts, allRequiredLegacy),
    adaptLegacy(profileUploader, allRequiredLegacy),
    adaptLegacy(profileMerge, allRequiredLegacy),
    adaptLegacy(search, allRequiredLegacy),
    adaptLegacy(settings, allRequiredLegacy),
    adaptLegacy(teams, allRequiredLegacy),
    adaptLegacy(teamSettings, allRequiredLegacy),
    adaptLegacy(expertRequestList, agreementsWithEmailRequiredLegacy),
    adaptLegacy(register, agreementsRequiredLegacy),
    // Before `admin`, whose `/admin/:model?/:id?` would otherwise be the only
    // thing under /admin. React Router ranks static segments above dynamic ones
    // so order is not what decides this — but reading them adjacently is how the
    // next person sees that they share a prefix at all. Same super-admin gate:
    // the console is a staff tool that moves money.
    adaptLegacy(expertPayments, superAdminRequiredLegacy),
    adaptLegacy(admin, superAdminRequiredLegacy),
    ...validateEmail.map((route) => adaptLegacy(route, loginRequiredLegacy)),
    ...expertNetwork.map((route) => adaptLegacy(route, loginRequiredLegacy)),
    ...projectDetails.map((route) => adaptLegacy(route, agreementsWithEmailRequiredLegacy)),
    adaptLegacy(team, allRequiredLegacy),
    adaptLegacy(teamAbout, allRequiredLegacy),
    ...complianceTraining.map((route) => adaptLegacy(route, allRequiredLegacy)),
    {
      path: '*',
      element: <NotFoundPage />,
    },
  ];
}

function withErrorElement(routes: RouteObject[]): RouteObject[] {
  return routes.map((route): RouteObject => {
    const errorElement = route.errorElement ?? <RouteErrorElement />;
    if (route.index) {
      return { ...route, errorElement };
    }
    return {
      ...route,
      errorElement,
      children: route.children ? withErrorElement(route.children) : route.children,
    };
  });
}

export default (context: MiddlewareContext): RouteObject[] => {
  const accessRoutes = createAccessRoutes(context);
  return withErrorElement([...accessRoutes, ...harnessRoutes]);
};
